import React from "react";
import { Box, Typography } from "@mui/material";
import InviteInstructions from "./InviteInstructions";
import type { InviteIntegrationSource, InvitePlatform } from "./inviteTypes";
import type { InviteBodyKind } from "./inviteSectionConfig";
import InviteCallout from "./InviteCallout";
import { inviteTextBoldSx, inviteTextSx } from "./inviteTypography";

export interface InviteBodySlotProps {
  kinds: InviteBodyKind[];
  platform: InvitePlatform;
  integrationSource?: InviteIntegrationSource;
  podcastTitle?: string;
  companyName?: string;
}

const Emphasis: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={inviteTextBoldSx}>
    {children}
  </Box>
);

export const InviteInactiveContext: React.FC<{
  integrationSource?: InviteIntegrationSource;
  podcastTitle?: string;
  companyName?: string;
}> = ({ integrationSource, podcastTitle, companyName }) => (
  <InviteCallout>
    <Typography variant="body2" sx={inviteTextSx}>
      {integrationSource === "prixi" ? (
        <>
          {podcastTitle ? (
            <>
              Conexión para <Emphasis>{podcastTitle}</Emphasis> en Prixi.
            </>
          ) : (
            <>Conexión de cuenta para Prixi.</>
          )}{" "}
          Si crees que esto es un error, genera un enlace nuevo desde Prixi →
          Cuentas conectadas.
        </>
      ) : podcastTitle && companyName ? (
        <>
          Invitación para <Emphasis>{podcastTitle}</Emphasis> de{" "}
          <Emphasis>{companyName}</Emphasis>.
        </>
      ) : companyName ? (
        <>
          Invitación de <Emphasis>{companyName}</Emphasis>.
        </>
      ) : (
        <>
          Si crees que esto es un error, pide al equipo del podcast que genere un
          enlace nuevo desde Configuración → Cuentas conectadas.
        </>
      )}
    </Typography>
  </InviteCallout>
);

export const InviteDisconnectedHint: React.FC<{
  integrationSource?: InviteIntegrationSource;
}> = ({ integrationSource }) => (
  <InviteCallout>
    <Typography variant="body2" sx={inviteTextSx}>
      {integrationSource === "prixi"
        ? "Vuelve a Prixi → Cuentas conectadas para generar un nuevo enlace."
        : "Si necesitas volver a conectar, solicita un nuevo enlace de invitación."}
    </Typography>
  </InviteCallout>
);

export const InviteDisconnectInfo: React.FC<{
  variant: "success" | "already_connected";
  integrationSource?: InviteIntegrationSource;
}> = ({ variant, integrationSource }) => {
  if (integrationSource === "prixi") {
    if (variant === "success") {
      return (
        <InviteCallout variant="caution">
          <Typography variant="body2" sx={inviteTextSx}>
            Listo. Vuelve a la pestaña de Prixi. Cerrando esta pestaña
            automáticamente…
          </Typography>
          <Typography
            variant="body2"
            sx={{ ...inviteTextSx, color: "text.secondary", mt: 1 }}
          >
            Podscope almacena el acceso de solo lectura en nombre de Prixi.
          </Typography>
        </InviteCallout>
      );
    }

    return (
      <InviteCallout>
        <Typography variant="body2" sx={inviteTextSx}>
          Cierra esta pestaña y continúa en Prixi.
        </Typography>
      </InviteCallout>
    );
  }

  if (variant === "success") {
    return (
      <InviteCallout variant="caution">
        <Typography variant="body2" sx={inviteTextSx}>
          Para desconectar la cuenta, deberás volver a autenticarte por seguridad.
        </Typography>
      </InviteCallout>
    );
  }

  return (
    <InviteCallout>
      <Typography variant="body2" sx={inviteTextSx}>
        Puedes desconectarla y eliminar todos los datos de Podscope.
      </Typography>
    </InviteCallout>
  );
};

export const InviteBodySlot: React.FC<InviteBodySlotProps> = ({
  kinds,
  platform,
  integrationSource = "podscope",
  podcastTitle,
  companyName,
}) => {
  if (kinds.length === 0) return null;

  return (
    <>
      {kinds.includes("instructions") && (
        <InviteInstructions platform={platform} />
      )}
      {kinds.includes("meta-connect-instructions") && (
        <InviteInstructions platform={platform} instructionSet="meta-connect" />
      )}
      {kinds.includes("meta-mapping-instructions") && (
        <InviteInstructions platform={platform} instructionSet="meta-mapping" />
      )}
      {kinds.includes("meta-page-id-fallback") && (
        <InviteInstructions
          platform={platform}
          instructionSet="meta-page-id-fallback"
        />
      )}
      {kinds.includes("disconnect-info") && (
        <InviteDisconnectInfo
          variant="success"
          integrationSource={integrationSource}
        />
      )}
      {kinds.includes("disconnect-info-already") && (
        <InviteDisconnectInfo
          variant="already_connected"
          integrationSource={integrationSource}
        />
      )}
      {kinds.includes("inactive-context") && (
        <InviteInactiveContext
          integrationSource={integrationSource}
          podcastTitle={podcastTitle}
          companyName={companyName}
        />
      )}
      {kinds.includes("disconnected-hint") && (
        <InviteDisconnectedHint integrationSource={integrationSource} />
      )}
    </>
  );
};

export default InviteBodySlot;
